import React, { useState, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import {
	Alert,
	Button,
	Tooltip,
	notification,
	Tag,
	Progress,
	ProgressProps,
} from "antd"
import { API, Endpoints } from "utils/api"
import { getStatusLabel, getStatusColor } from "utils/status"
import { Route, RouteStatus, RouteExternalEligibility } from "../types"
import Strings from "utils/strings"
import { ALL_CONTRACTS_VALUE } from "hooks"

interface TransitWarningProps {
	loading: boolean
	gradientStatusColors: ProgressProps["strokeColor"]
	onCancel: () => void
	onConfirm: () => void
}

const TransitWarning: React.FC<TransitWarningProps> = ({
	loading,
	gradientStatusColors,
	onCancel,
	onConfirm,
}) => (
	<div className="w-100">
		<div
			style={{
				padding: "10px 12px",
				display: "flex",
				flexDirection: "column",
				gap: 8,
			}}
		>
			<Alert
				type="warning"
				showIcon
				message={Strings.routes.transitWarningMessage}
				style={{ fontSize: 12 }}
			/>
			<div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
				<Button size="small" onClick={onCancel}>
					{Strings.generic.cancel}
				</Button>
				<Button
					size="small"
					type="primary"
					danger
					loading={loading}
					onClick={onConfirm}
				>
					{Strings.generic.confirm}
				</Button>
			</div>
		</div>
		<Progress
			percent={100}
			status="active"
			showInfo={false}
			strokeColor={gradientStatusColors}
			style={{
				opacity: loading ? 0.5 : 0,
				transition: "opacity 0.3s ease-in-out",
			}}
		/>
	</div>
)

interface ChangeStatusButtonProps {
	routes: Route[] // Support both single route (array of 1) and multiple routes
	setRoutes: React.Dispatch<React.SetStateAction<Route[]>>
}

const ChangeStatusButton: React.FC<ChangeStatusButtonProps> = ({
	routes,
	setRoutes,
}) => {
	const [loading, setLoading] = useState(false)
	const [canChangeStatus, setCanChangeStatus] = useState(true)
	const [validationErrors, setValidationErrors] = useState<string[]>([])
	const [showTransitWarning, setShowTransitWarning] = useState(false)

	// Redux variables
	const rawFilters = useSelector((state: any) => state.filters) || {}
	const { business, contract } = useMemo(
		() => ({
			business: rawFilters.business,
			contract: rawFilters.contract,
		}),
		[rawFilters.business, rawFilters.contract]
	)

	// Check if all routes have the same status
	const commonStatus = useMemo(() => {
		if (routes.length === 0) return null
		const firstStatus = routes[0].status
		const allSameStatus = routes.every((route) => route.status === firstStatus)
		return allSameStatus ? firstStatus : null
	}, [routes])

	// Define status flow
	const getNextStatus = (currentStatus: string): string | null => {
		const statusFlow: Record<string, string> = {
			aggregation: "loading",
			loading: "ready_to_van",
			ready_to_van: "dispatching",
			dispatching: "on_transit",
		}
		return statusFlow[currentStatus] || null
	}

	const nextStatus = commonStatus ? getNextStatus(commonStatus) : null

	// Validate requirements for status change
	useEffect(() => {
		if (!commonStatus) {
			setValidationErrors([Strings.routes.allRoutesSameStatus])
			setCanChangeStatus(false)
			return
		}

		// "All contracts" has no single contract to scope the update to.
		if (contract === ALL_CONTRACTS_VALUE) {
			setValidationErrors([Strings.routes.contractRequiredForAction])
			setCanChangeStatus(false)
			return
		}

		const validateStatusChange = () => {
			const errors: string[] = []

			// Validate each route
			routes.forEach((route, index) => {
				const routePrefix = routes.length > 1 ? `Route ${index + 1}: ` : ""

				switch (route.status) {
					case "loading":
						// Requirements: Route must have addresses
						if (!route.addresses || route.addresses.length === 0) {
							errors.push(`${routePrefix}${Strings.routes.mustHaveAddresses}`)
						}
						break

					case "aggregation":
						// Requirements: All addresses must have valid locations
						const addressesWithoutLocation = route.addresses.filter(
							(addr) => !addr.location?.lat || !addr.location?.lng
						)
						if (addressesWithoutLocation.length > 0) {
							errors.push(
								`${routePrefix}${Strings.routes.addressesMissingLocation.replace("{0}", addressesWithoutLocation.length.toString())}`
							)
						}
						break

					case "ready_to_van":
						break

					case "dispatching":
						// Requirements: Driver must be assigned
						if (!route.driver) {
							errors.push(
								`${routePrefix}${Strings.routes.driverMustBeAssigned}`
							)
						}
						// Requirements: Vehicle must be assigned
						if (!route.vehicle) {
							errors.push(
								`${routePrefix}${Strings.routes.vehicleMustBeAssigned}`
							)
						}
						// Requirements: Grid must be assigned (for van routes)
						if (route.routeType === "van" && !route.gradeId) {
							errors.push(`${routePrefix}${Strings.routes.gridMustBeAssigned}`)
						}
						// Requirements: Route must have valid addresses
						if (!route.addresses || route.addresses.length === 0) {
							errors.push(
								`${routePrefix}${Strings.routes.mustHaveDeliveryAddresses}`
							)
						}

						// Requirements: Date cannot be in the past
						const today = new Date()
						today.setHours(0, 0, 0, 0)
						const routeDate = new Date(route.date)
						routeDate.setHours(0, 0, 0, 0)
						if (routeDate.getTime() !== today.getTime()) {
							errors.push(
								`${routePrefix}${Strings.routes.notScheduledForToday}`
							)
						}
						break

					case "on_transit":
						// Final status - cannot change further
						errors.push(`${routePrefix}${Strings.routes.alreadyOnTransit}`)
						break
				}
			})

			setValidationErrors(errors)
			setCanChangeStatus(errors.length === 0 && nextStatus !== null)
		}

		validateStatusChange()
	}, [routes, nextStatus, commonStatus, contract])

	const handleChangeStatus = async () => {
		if (!commonStatus || !canChangeStatus) return

		setLoading(true)

		try {
			const routeIds = routes.map((route) => route._id)

			const response = await API.patch({
				url: Endpoints.v2.uriRoutes({ routeId: "status" }),
				data: {
					status: commonStatus,
					routeIds,
				},
				headers: {
					business,
					contract,
				},
			})

			if (response.ok) {
				// The status endpoint recomputes external-delivery eligibility for the changed routes
				const eligibilityById = new Map<string, RouteExternalEligibility>(
					(
						(response.data?.results?.routesExternalEligibility || []) as Array<{
							routeId: string
							externalEligibility: RouteExternalEligibility
						}>
					).map((e) => [e.routeId, e.externalEligibility])
				)

				setRoutes((prevRoutes) => {
					return prevRoutes.map((r) => {
						if (!routeIds.includes(r._id)) return r

						return {
							...r,
							status: nextStatus as RouteStatus,
							externalEligibility:
								eligibilityById.get(r._id) ?? r.externalEligibility,
							addresses: r.addresses.map((addr) => {
								return {
									...addr,
									order: {
										...addr.order,
										status: nextStatus as any,
										bundledOrders:
											addr.order.bundledOrders?.map((bo) => {
												return {
													...bo,
													status: nextStatus as any,
												}
											}) || [],
									},
								}
							}),
						}
					})
				})
				notification.success({
					message: Strings.routes.statusUpdated,
					description:
						routes.length === 1
							? Strings.routes.statusUpdateSuccess.replace(
									"{0}",
									getStatusLabel(nextStatus as RouteStatus)
								)
							: Strings.routes.statusUpdateSuccessMultiple
									.replace("{0}", routes.length.toString())
									.replace("{1}", getStatusLabel(nextStatus as RouteStatus)),
					placement: "bottomRight",
					duration: 3,
				})
			} else {
				notification.error({
					message: Strings.routes.updateFailed,
					description:
						response?.data?.message || Strings.routes.statusUpdateError,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.routes.updateFailed,
				description: Strings.routes.updateError,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setLoading(false)
		}
	}

	if (!nextStatus || !commonStatus || commonStatus === "aggregation") {
		return null
	}

	const currentStatusColor = getStatusColor(commonStatus as RouteStatus)
	const nextStatusColor = getStatusColor(nextStatus as RouteStatus)

	const gradientStatusColors: ProgressProps["strokeColor"] = {
		"0%": `${currentStatusColor}`,
		"100%": `${nextStatusColor}`,
	}

	const isTransit = nextStatus === "on_transit"

	if (showTransitWarning) {
		return (
			<TransitWarning
				loading={loading}
				gradientStatusColors={gradientStatusColors}
				onCancel={() => setShowTransitWarning(false)}
				onConfirm={() => {
					setShowTransitWarning(false)
					handleChangeStatus()
				}}
			/>
		)
	}

	return (
		<div className="w-100">
			<Tooltip
				title={
					!canChangeStatus && validationErrors.length > 0 ? (
						<div>
							<div style={{ fontWeight: "bold", marginBottom: 4 }}>
								{Strings.routes.statusChangeRequirements}
							</div>
							<ul style={{ margin: 0, paddingLeft: 20 }}>
								{validationErrors.map((error, index) => (
									<li key={index}>{error}</li>
								))}
							</ul>
						</div>
					) : routes.length === 1 ? (
						Strings.routes.changeStatusTo.replace(
							"{0}",
							getStatusLabel(nextStatus as RouteStatus)
						)
					) : (
						Strings.routes.changeMultipleRoutesStatus
							.replace("{0}", routes.length.toString())
							.replace("{1}", getStatusLabel(nextStatus as RouteStatus))
					)
				}
			>
				<Button
					type="text"
					// disabled={!canChangeStatus} // Causes problems with spacing
					onClick={() => {
						if (!canChangeStatus) return
						if (isTransit) {
							setShowTransitWarning(true)
							return
						}
						handleChangeStatus()
					}}
					style={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						gap: 6,
						padding: "10px 12px 0",
						height: "auto",
						border: "none",
						background: "transparent",
						opacity: canChangeStatus ? 1 : 0.5,
						cursor: canChangeStatus ? "pointer" : "not-allowed",
					}}
				>
					<Tag
						color={currentStatusColor}
						style={{
							margin: 0,
							fontSize: 12,
							fontWeight: 500,
							opacity: canChangeStatus ? 1 : 0.8,
						}}
					>
						{getStatusLabel(commonStatus as RouteStatus)}
					</Tag>
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						style={{
							color: canChangeStatus ? "#1890ff" : "#999",
							margin: "0 10px",
						}}
					>
						<path
							d="M5 12H19M19 12L12 5M19 12L12 19"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					<Tag
						color={nextStatusColor}
						style={{
							margin: 0,
							fontSize: 12,
							fontWeight: 500,
							opacity: canChangeStatus ? 1 : 0.8,
						}}
					>
						{getStatusLabel(nextStatus as RouteStatus)}
					</Tag>
				</Button>
			</Tooltip>
			<Progress
				percent={100}
				status="active"
				showInfo={false}
				strokeColor={gradientStatusColors}
				style={{
					opacity: loading ? 0.5 : 0,
					transition: "opacity 0.3s ease-in-out",
				}}
			/>
		</div>
	)
}

export default ChangeStatusButton
